// ── Lecture des champs d'un event : un seul point de verite ──────────────
// Avant l'audit DATA du 2026-08-25, `parsePrice` existait en 8 exemplaires
// (design-home, salle, genre, artiste, compte, cmdk-search, guide-view,
// guides) avec 3 comportements differents, le lien billetterie etait
// recalcule a 8 endroits et le format d'heure teste a 6. Chaque correction
// n'en reparait qu'une copie. Tout passe desormais par ce module, charge en
// `defer` avant les modules de page (comme venues-data.js).
(function (global) {
  'use strict';

  // ── Heure ─────────────────────────────────────────────────────────────
  // `time` coexiste en deux formats en base : « 20:00 » et « 20h00 » (136
  // events, Salle Pleyel). Le test ancre sur `HH:MM` rejetait le second :
  // l'heure, pourtant connue, n'etait affichee nulle part.
  function fmtTime(t) {
    var m = String(t == null ? '' : t).trim().match(/^(\d{1,2})\s*[h:]\s*(\d{2})/i);
    if (!m) return '';
    var hh = Number(m[1]), mm = Number(m[2]);
    if (hh > 23 || mm > 59) return '';
    return String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
  }

  // ── Prix ──────────────────────────────────────────────────────────────
  // Au centime pres (chantier prix, 2026-10-20) : un prix affiche doit se
  // retrouver sur la page de la billetterie. L'arrondi a l'euro montrait
  // « 99 € » pour 99,40 € (moins que ce que paie l'acheteur) et « 90 € »
  // pour 89,50 €. Meme ecriture que les pages pre-rendues
  // (generate_seo_pages._fmt_eur) et que le portage city_seo._euro.
  function fmtEuro(v) {
    var c = Math.round(Number(v) * 100);
    if (!isFinite(c)) return '';
    return (c % 100 === 0 ? String(c / 100) : (c / 100).toFixed(2).replace('.', ',')) + ' €';
  }

  // Les colonnes normalisees a l'ingestion (price_min_cents / price_max_cents
  // / price_parse_status, scrapers/price_parse.py) priment sur le re-parsing
  // de la chaine brute. Et « des 40 € » ne devient plus « 40 € » : le libelle
  // perdait son plancher sur 86 events, dont un affiche 35 € pour une
  // fourchette reelle de 35 a 134 €.
  function parsePrice(p, e) {
    var raw = String(p == null ? '' : p).trim();
    var status = e && e.price_parse_status;
    var minC = e && Number.isFinite(e.price_min_cents) ? e.price_min_cents : null;
    var maxC = e && Number.isFinite(e.price_max_cents) ? e.price_max_cents : null;
    var isFrom = status === 'from' || /\b(d[eè]s|[aà]\s+partir\s+de|min\.?)\b/i.test(raw);

    if (minC !== null) {
      var min = minC / 100;
      if (min === 0 && !maxC) return { value: 0, label: 'Gratuit' };
      if (maxC !== null && maxC > minC) {
        return { value: min, label: fmtEuro(min) + ' – ' + fmtEuro(maxC / 100), from: true };
      }
      return { value: min, label: isFrom ? 'dès ' + fmtEuro(min) : fmtEuro(min), from: isFrom };
    }

    // Le bouton n'a pas de prix, un autre canal en a un (decision du
    // 2026-10-21, « on affiche ce qu'on a, quitte a le marquer ») : l'export
    // pose `price_other_channel` ({cents, platform}, prix paye sur ce canal,
    // frais compris quand on les connait ; public_payload.
    // other_channel_price_field). Affiche marque, « dès 37 € sur Dice » —
    // la mention « sur Dice » de la grille de la fiche (#770). `value` reste
    // null : ce n'est pas le prix du bouton, il ne trie ni ne filtre rien
    // (les bornes, l'Offer et le barometre l'ignorent aussi). Miroirs :
    // city_seo.parse_price, generate_seo_pages.other_channel_price_label.
    var other = e && e.price_other_channel;
    if (other && Number.isFinite(other.cents) && other.cents > 0 && other.platform) {
      return { value: null, label: 'dès ' + fmtEuro(other.cents / 100) + ' sur ' + other.platform,
               from: true, other: String(other.platform) };
    }

    if (!raw) return { value: null, label: '—' };
    if (/gratuit/i.test(raw)) return { value: 0, label: 'Gratuit' };
    // Statut normalise pose sans borne : le parseur de l'ingestion a lu ce
    // texte et n'y a trouve aucun prix a afficher (« Voir site », un numero
    // de telephone, le prix d'une autre billetterie ecarte). On ne refabrique
    // pas un nombre a la lecture (chantier prix, 2026-10-20).
    if (status) return { value: null, label: raw.length > 14 ? 'Voir' : raw };
    var range = raw.match(/(\d+(?:[.,]\d+)?)\s*(?:€|EUR)?\s*(?:-|–|[aà])\s*(\d+(?:[.,]\d+)?)/i);
    if (range) {
      var lo = parseFloat(range[1].replace(',', '.')), hi = parseFloat(range[2].replace(',', '.'));
      if (hi > lo) return { value: lo, label: fmtEuro(lo) + ' – ' + fmtEuro(hi), from: true };
    }
    var m = raw.match(/(\d+(?:[.,]\d+)?)\s*€?/);
    if (m) {
      var v = parseFloat(m[1].replace(',', '.'));
      return { value: v, label: isFrom ? 'dès ' + fmtEuro(v) : fmtEuro(v), from: isFrom };
    }
    return { value: null, label: raw.length > 14 ? 'Voir' : raw };
  }

  // ── Lien billetterie ──────────────────────────────────────────────────
  // Le bouton « Billetterie » partait sur `link` (le site de la salle) dans
  // 1579 cas sur 1608 : il n'ouvrait pas de billetterie. Mais `ticket_url`
  // n'est pas fiable non plus — sur un controle de 14 concerts, 5 pointaient
  // vers une page Facebook de salle, un panier vide, une page de resultats de
  // recherche ou une page salle generique. On prend donc `ticket_url` quand il
  // designe vraiment un evenement, `link` sinon.
  var BAD_TICKET_URL = [
    /facebook\.com|instagram\.com|twitter\.com|\/\/x\.com/i,  // reseau social
    /\/(cart|panier|basket)(\/|$|\?)/i,                       // panier vide
    /\/(identification|login|connexion|mon-compte|mes-billets|account|compte)(\/|$|\?)/i,  // connexion / compte (miroir de DEAD_END_SEGMENTS)
    /[?&](ipSearch|search|q|query)=/i,                        // page de resultats
    /shotgun\.live\/(fr\/)?venues\//i                         // page salle Shotgun
  ];

  function isUsableTicketUrl(u) {
    var s = String(u == null ? '' : u).trim();
    if (!/^https?:\/\//i.test(s)) return false;
    for (var i = 0; i < BAD_TICKET_URL.length; i++) {
      if (BAD_TICKET_URL[i].test(s)) return false;
    }
    try {
      var path = new URL(s).pathname;
      if (!path || path === '/') return false;   // racine de domaine
    } catch (_) { return false; }
    return true;
  }

  // ── Revente d'une date complete (decision produit du 2026-10-05) ───────
  // Quand une date est complete et qu'on connait sa bourse de revente, le
  // bouton de billetterie y mene, libelle « Revente » — jamais « Reserver ».
  // La regle vit dans l'export (scrapers/public_payload.py,
  // sold_out_resale_url) : `sold_out_resale_url` n'est pose que sur une date
  // `sold_out` CONFIRMEE (pas `status_uncertain`), a venir, qui a un
  // `resale_url`. Ici on ne fait que le suivre ; miroir
  // cote pages pre-rendues : public_payload.RESALE_CTA_LABEL.
  var RESALE_LABEL = 'Revente';

  function resaleHref(e) {
    var u = String((e && e.sold_out_resale_url) || '').trim();
    return /^https?:\/\//i.test(u) ? u : '';
  }

  function ticketHref(e) {
    if (!e) return '';
    var resale = resaleHref(e);
    if (resale) return resale;
    if (isUsableTicketUrl(e.ticket_url)) return e.ticket_url;
    return e.link || e.ticket_url || '';
  }

  /** Libelle du bouton d'une date complete : « Revente » si le bouton mene a
   *  la revente (ticketHref), `fallback` sinon (« Voir le site »...). */
  function soldOutCtaLabel(e, fallback) {
    return resaleHref(e) ? RESALE_LABEL : fallback;
  }

  // ── Nom public de la billetterie ──────────────────────────────────────
  // `ticketing_platform` porte un marqueur INTERNE : « Bataclan (propre) »
  // dit que la salle vend elle-meme (lu par l'analytics). Il ne s'affiche
  // jamais tel quel : la fiche concert montrait « Billetterie · Bataclan
  // (propre) » (releve du 2026-10-20). Miroir : platform_label
  // (generate_seo_pages), meme libelle cote pages statiques.
  var OWN_TICKETING_LABEL = 'Billetterie officielle de la salle';
  var OWN_MARKER_RE = /\(propre\)\s*$/i;

  /** La plateforme telle qu'un visiteur peut la lire, '' pour la billetterie
   *  de la salle (le marqueur interne) ou faute d'etiquette. */
  function platformLabel(e) {
    var p = String((e && e.ticketing_platform) || '').trim();
    return OWN_MARKER_RE.test(p) ? '' : p;
  }

  /** La salle vend-elle elle-meme (etiquette « … (propre) ») ? */
  function isOwnTicketing(e) {
    return OWN_MARKER_RE.test(String((e && e.ticketing_platform) || '').trim());
  }

  /** Libelle du bouton d'achat d'une date en vente : « Billetterie officielle
   *  de la salle », « Billetterie · Dice », ou « Billetterie ». */
  function ticketCtaLabel(e) {
    if (isOwnTicketing(e)) return OWN_TICKETING_LABEL;
    var p = platformLabel(e);
    return p ? 'Billetterie · ' + p : 'Billetterie';
  }

  /** `data-platform` du bouton (lu par perf.js, ticket_click) : « Revente »
   *  quand il mene a la revente, la plateforme de billetterie sinon. */
  function ctaPlatform(e) {
    if (resaleHref(e)) return RESALE_LABEL;
    // Canal du bouton epuise, revente ouverte : le clic part en revente
    // (channelSoldOut, plus bas — hissee, appelee au clic seulement).
    var c = channelSoldOut(e);
    if (c && c.resale) return RESALE_LABEL;
    return (e && e.ticketing_platform) || '';
  }

  // ── Fraicheur du « Complet » ──────────────────────────────────────────
  // `sold_out` est sticky : une fois pose, rien ne le levait automatiquement
  // sauf un compteur de places, que 1 scraper sur 20 fournit. 195 concerts
  // etaient donc figes « Complet », dont un faux positif verifie le
  // 2026-08-25 (Evanescence a l'Accor Arena, remis en vente depuis mars).
  //
  // db_manager marque desormais `status_uncertain` quand la source n'a plus
  // reconfirme le complet depuis 14 jours. On n'affiche pas « en vente » pour
  // autant — faire deplacer quelqu'un pour rien serait pire. Le libelle reste
  // « Complet » : le « Complet ? » affiche jusqu'au 2026-09-16 jetait le doute
  // sur des dizaines de concerts a la fois (decision produit). Le doute et la
  // date du dernier constat ne vivent plus que dans l'infobulle.
  function isUncertain(e) {
    return !!e && (e.status_uncertain === true || e.status_uncertain === 1);
  }

  function soldOutSince(e) {
    var raw = (e && e.sold_out_since) || '';
    return String(raw).slice(0, 10);
  }

  function frDate(iso) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return '';
    var d = new Date(iso + 'T00:00:00');
    if (isNaN(d)) return '';
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  // Nouvelle date d'un report (decision du 2026-10-25) : `rescheduled_from`
  // pose, cette date EST la nouvelle — elle aura lieu, au bon jour. Elle ne
  // dit donc plus « Reporte », reserve a la date qui n'aura pas lieu
  // (`postponed` sans `rescheduled_from`, ou nouvelle date reportee a son tour,
  // `postponed_again`), mais « Nouvelle date », avec la date d'origine.
  // `rescheduled_from` garde l'origine la plus ancienne d'une chaine de
  // reports : « initialement prevu le », juste meme apres deux reports — sauf
  // une chaine revenue a son jour d'origine (26/02 -> 16/02 -> 26/02, La P'tite
  // Fumee au Silex) : `rescheduled_from` == `date`, ce n'est pas une nouvelle
  // date, et les modules ne la comptent pas comme reportee (`postponed` lit
  // newDate, pas `rescheduled_from`).
  // Miroir cote pages pre-rendues : city_seo.new_date_label.
  /** {short, long, from} ou null. `short` : badges et colonnes etroites ;
   *  `long` : infobulle ; `from` : la date d'origine en toutes lettres
   *  (« 2 décembre 2025 », '' si illisible). */
  function newDate(e) {
    if (!e || !e.rescheduled_from || e.postponed_again) return null;
    if (e.sale_status === 'cancelled' || e.cancelled === true || e.cancelled === 1) return null;
    var origin = String(e.rescheduled_from).slice(0, 10);
    if (origin === String(e.date || '').slice(0, 10)) return null;
    var from = frDate(origin);
    return { short: 'Nouvelle date', from: from,
             long: from ? 'Nouvelle date — initialement prévu le ' + from : 'Nouvelle date' };
  }

  // Complet contredit (regle 4 de scrapers/channel_display.py, 2026-10-22) :
  // la salle redit « Complet », un canal de la date la disait en vente il y a
  // deux jours au plus. Le libelle reste « Complet » ; l'infobulle le dit,
  // avec le canal et la date du releve (jamais un nombre de places).
  function stillSelling(e) {
    var c = e && e.sold_out_still_selling;
    return (c && typeof c === 'object' && e.sale_status === 'sold_out') ? c : null;
  }

  /** Libelle du statut complet : {short, long}. `long` va en infobulle. */
  function soldOutLabel(e) {
    var since = frDate(soldOutSince(e));
    var long = since ? 'Complet depuis le ' + since : 'Complet';
    var selling = stillSelling(e);
    if (selling) {
      var seen = frDate(String(selling.seen || '').slice(0, 10));
      var name = selling.own ? 'la billetterie de la salle' : (selling.platform || 'la billetterie');
      long = 'Annoncé complet par la salle ; des billets étaient encore en vente sur ' + name
          + (seen ? ' le ' + seen : '') + ' — vérifiez sur la billetterie.';
      return { short: 'Complet', long: long };
    }
    if (isUncertain(e)) {
      long = (since ? 'Annoncé complet depuis le ' + since + '. ' : '')
          + 'Non reconfirmé par la salle depuis plus de 14 jours — vérifiez sur la billetterie.';
    }
    return { short: 'Complet', long: long };
  }

  // ── Canal du bouton epuise (decisions du 2026-10-15 et du 2026-10-21) ──
  // Le canal que le bouton ouvre ne vend plus (etat `sold_out` confirme :
  // deux releves de deux jours distincts), aucun autre canal connu n'est en
  // vente, et la date n'est ni complete, ni annulee, ni reportee : l'export
  // pose `channel_sold_out` ({platform, own, since, seen, resale}), absent
  // sinon (scrapers/channel_display.py, la regle vit la-bas). Ce n'est PAS un
  // complet de la date (option B du 21/10) : `sold` reste faux, et le canal
  // est nomme — « Complet sur la billetterie de la salle » (`own`),
  // « Complet chez Ticketmaster ». Quand un autre canal vend (regle 2),
  // l'export a deja mis son lien dans `ticket_url` : rien a faire ici.
  // Miroir cote pages pre-rendues : channel_display.label / short_label /
  // tooltip / cta_label (generate_seo_pages).
  var CHANNEL_SHORT = { Ticketmaster: 'TM', 'Fnac Spectacles': 'Fnac' };
  var OWN_CHANNEL = 'la billetterie de la salle';

  /** {label, short, long, resale, platform, own} ou null. `label` : badge et
   *  bouton (« Complet chez Ticketmaster ») ; `short` : colonnes etroites
   *  (« Complet TM », « Complet billetterie ») ; `long` : infobulle
   *  datee. */
  function channelSoldOut(e) {
    var c = e && e.channel_sold_out;
    if (!c || typeof c !== 'object') return null;
    // Le statut de la date prime toujours (l'export ne le pose pas dessus).
    if (e.sale_status === 'sold_out' || e.sale_status === 'cancelled'
        || e.sale_status === 'postponed') return null;
    var own = !!c.own;
    var platform = own ? OWN_CHANNEL : String(c.platform || 'Ticketmaster');
    var seen = frDate(String(c.seen || '').slice(0, 10));
    var since = frDate(String(c.since || '').slice(0, 10));
    var long = 'Plus de billets en vente sur ' + platform + (seen ? ' le ' + seen : '')
      + (since && since !== seen ? ' (épuisé depuis le ' + since + ')' : '')
      + (own ? " ; la date n'est pas annoncée complète." : " ; la salle ne l'annonce pas.");
    if (c.resale) long += ' Revente ouverte sur ' + platform + '.';
    return {
      label: own ? 'Complet sur ' + OWN_CHANNEL : 'Complet chez ' + platform,
      short: own ? 'Complet billetterie' : 'Complet ' + (CHANNEL_SHORT[platform] || platform),
      long: long,
      resale: !!c.resale,
      platform: platform,
      own: own
    };
  }

  /** Libelle du bouton d'une date dont le canal du bouton est epuise (regle
   *  3 : le bouton y mene) : « Revente » quand la revente y est ouverte,
   *  sinon le canal ou c'est complet (« Complet sur la billetterie de la
   *  salle », « Complet chez Ticketmaster ») — jamais « Reserver », le meme
   *  libelle que la fiche statique (channel_display.cta_label). `fallback`
   *  hors regle 3. */
  function channelCtaLabel(e, fallback) {
    var c = channelSoldOut(e);
    if (!c) return fallback;
    return c.resale ? RESALE_LABEL : c.label;
  }

  /** Libelle horaire : heure de scene, et ouverture des portes si connue. */
  function timeLabel(e) {
    var start = fmtTime(e && e.time);
    var doors = fmtTime(e && e.door_time);
    if (start && doors && doors !== start) return start + ' (portes ' + doors + ')';
    if (start) return start;
    if (doors) return 'portes ' + doors;
    return '';
  }

  // ── Horizon ───────────────────────────────────────────────────────────
  // events_upcoming.json part du 1er janvier pour que le calendrier couvre
  // toute l'annee civile : 24 % de son contenu est deja passe. Tout ce qui
  // compte ou resume doit passer par ici.
  function isUpcoming(e, ref) {
    var now = ref || new Date(); now.setHours(0, 0, 0, 0);
    var d = e && (e.dateObj || (e.date ? new Date(e.date + 'T00:00:00') : null));
    return !!d && d >= now;
  }

  function upcomingOnly(list) {
    var now = new Date(); now.setHours(0, 0, 0, 0);
    return (list || []).filter(function (e) { return isUpcoming(e, new Date(now)); });
  }

  // ── Producteurs observes (piege 7) ────────────────────────────────────
  // `producer` des payloads porte tous les liens de role producteur, dont
  // les `source='inferred'` que producer_lookup deduit de la memoire
  // artiste : ils disent qui booke l'artiste EN GENERAL, jamais qui a
  // produit CETTE date. Les pages pre-rendues n'attribuent que les liens
  // observes, mais les modules qui s'hydratent par-dessus re-agregeaient le
  // champ brut : /producteur/base-productions/ annoncait « 15 concerts »
  // cote serveur et « 39 dates programmees » apres JS (69 dates inferees sur
  // 20 pages). generate_seo_pages.py publie a cote du payload, sans le
  // toucher, /data/producers_observed.json : {id: [producteurs observes]}
  // (scrapers/utils/observed_producers.py). Toute attribution d'une date a
  // un producteur passe par ici. Sans annexe (null : V2, dont build_v2.py
  // sert le payload province sous /data/ sans elle), le payload reste tel
  // quel ; une page pre-rendue n'en attribue alors aucune (voir appelants).
  var observedReq = null;
  function loadObservedProducers() {
    if (!observedReq) {
      observedReq = fetch('/data/producers_observed.json')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (m) { return m && typeof m === 'object' && !Array.isArray(m) ? m : null; })
        .catch(function () { return null; });
    }
    return observedReq;
  }

  /** Reduit `producer` de chaque event, EN MEMOIRE, a ses liens observes.
   *  `observed` null (pas d'annexe) : liste inchangee ; {} : aucun producteur. */
  function keepObservedProducers(list, observed) {
    if (!observed) return list;
    var ref = observed;
    (list || []).forEach(function (e) {
      if (!e) return;
      var id = e.id == null ? null : String(e.id);
      var ok = id !== null && Object.prototype.hasOwnProperty.call(ref, id) && Array.isArray(ref[id]) ? ref[id] : [];
      var raw = Array.isArray(e.producer) ? e.producer : (e.producer ? [e.producer] : []);
      // Les noms inferes qui sortent restent affichables, marques (producerList).
      var usual = inferredNames(e).filter(function (p) { return ok.indexOf(p) === -1; });
      if (usual.length) e.producer_usual = usual;
      e.producer = raw.filter(function (p) { return ok.indexOf(p) !== -1; });
    });
    return list;
  }

  // ── Producteur habituel de l'artiste (audit du 2026-10-20) ────────────
  // Un lien infere n'attribue pas la date, mais il dit qui produit l'artiste
  // d'habitude : on l'affiche, marque, jamais comme un fait (regle « afficher,
  // mais marquer »). Les exports posent `producer_inferred` = les noms de
  // `producer` inferes (liens `source='inferred'` ou heritage d'export ;
  // `true` avant le 2026-10-20) ; keepObservedProducers range ceux qu'il
  // retire dans `producer_usual`. Miroir : USUAL_PRODUCER_LABEL /
  // USUAL_PRODUCER_TITLE de scrapers/generate_seo_pages.py (fiche pre-rendue).
  var USUAL_PRODUCER_LABEL = 'producteur habituel de l\u2019artiste';
  var USUAL_PRODUCER_TITLE = 'Non indiqu\u00e9 pour cette date : producteur relev\u00e9 sur les autres concerts de l\u2019artiste';
  function asList(v) {
    return (Array.isArray(v) ? v : (v ? [v] : [])).filter(function (x) { return typeof x === 'string' && x; });
  }
  /** Noms de `producer` marques inferes sur cet event. */
  function inferredNames(e) {
    if (!e) return [];
    var raw = asList(e.producer);
    if (e.producer_inferred === true) return raw;
    var flag = asList(e.producer_inferred);
    return raw.filter(function (p) { return flag.indexOf(p) !== -1; });
  }
  /** Producteurs a afficher pour une date : [{name, usual, href}], les lus d'abord.
   *  `usual` : producteur habituel de l'artiste (infere), a marquer ; `href`
   *  vide = pas de page publiee, texte simple. */
  function producerList(e) {
    if (!e) return [];
    var usual = inferredNames(e).concat(asList(e.producer_usual));
    var out = [], seen = {};
    asList(e.producer).concat(asList(e.producer_usual)).forEach(function (p) {
      if (p === 'Inconnu' || seen[p]) return;
      seen[p] = true;
      var isUsual = usual.indexOf(p) !== -1;
      // Un lu garde son lien (comme avant) ; un habituel seulement si sa page existe.
      out.push({ name: p, usual: isUsual,
                 href: (!isUsual || hasProducerPage(p)) ? producerHref(p) : '' });
    });
    return out.filter(function (x) { return !x.usual; }).concat(out.filter(function (x) { return x.usual; }));
  }

  // ── Page d'un producteur : celle de son groupe ────────────────────────
  // data/producer_groups.json : une filiale ou un autre nom d'une societe
  // (AUGURI PRODUCTIONS, TS3, FIMALAC) n'a pas de page /producteur/ a lui ;
  // sa page est celle du groupe (/producteur/aeg-presents-france/), comme
  // sur la fiche concert du serveur (generate_seo_pages
  // ._normalize_producer_to_parent). Le texte d'un lien reste le nom observe :
  // seule l'adresse change. Le fichier (5 Ko) est charge une fois, a la
  // demande : un module l'attend avant d'ecrire un lien producteur
  // (artiste.js, concert.js) ou le demande a son chargement, avant tout clic
  // (panneaux de salle.js, design-home.js, genre.js). Avant qu'il arrive,
  // producerHref() vise l'adresse du nom lui-meme, que producteur.js renvoie
  // vers son groupe.
  var groupsReq = null;
  var producerParent = {};          // nom en minuscules → nom du groupe
  function loadProducerGroups() {
    if (!groupsReq) {
      var req;
      try { req = fetch('/data/producer_groups.json'); } catch (_e) { req = Promise.reject(_e); }
      groupsReq = Promise.resolve(req)
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (j) {
          var groups = j && j.groups && typeof j.groups === 'object' ? j.groups : null;
          if (!groups) return null;
          Object.keys(groups).forEach(function (parent) {
            var g = groups[parent] || {};
            [].concat(g.subsidiaries || [], g.aliases || []).forEach(function (n) {
              if (n) producerParent[String(n).toLowerCase().trim()] = parent;
            });
          });
          return j;
        })
        .catch(function () { return null; });
      // La liste des pages /producteur/ publiees arrive avec : un module qui
      // attend les groupes avant d'ecrire un lien producteur l'a aussi.
      groupsReq = Promise.all([groupsReq, loadProducerPages()]).then(function (r) { return r[0]; });
    }
    return groupsReq;
  }
  // Pages /producteur/ publiees (dist/data/producer_pages.json, ecrit par
  // generate_producer_pages) : une page ne retient que les dates OBSERVEES,
  // donc un producteur « habituel » (infere) n'est un lien que si la sienne
  // existe — sinon BASE PRODUCTIONS menait a « Producteur introuvable ».
  // Meme regle que la fiche pre-rendue (producer_slugs). Fichier absent :
  // aucun lien pour un habituel (texte marque), jamais une adresse morte.
  var pagesReq = null;
  var producerPages = null;          // {slug: true} ou null
  function loadProducerPages() {
    if (!pagesReq) {
      var req;
      try { req = fetch('/data/producer_pages.json'); } catch (_e) { req = Promise.reject(_e); }
      pagesReq = Promise.resolve(req)
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (list) {
          if (!Array.isArray(list)) return null;
          producerPages = {};
          list.forEach(function (sl) { if (sl) producerPages[String(sl)] = true; });
          return producerPages;
        })
        .catch(function () { return null; });
    }
    return pagesReq;
  }
  /** La page /producteur/ de ce nom (celle de son groupe) est-elle publiee ? */
  function hasProducerPage(name) {
    return !!(producerPages && producerPages[producerSlugify(producerParentName(name))]);
  }
  function producerSlugify(text) {
    return String(text || '').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x00-\x7f]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  /** Nom du groupe d'un producteur (le nom lui-meme hors groupe). */
  function producerParentName(name) {
    var n = String(name || '').trim();
    return producerParent[n.toLowerCase()] || n;
  }
  /** Adresse de la page d'un producteur : celle de son groupe. */
  function producerHref(name) {
    return '/producteur/' + encodeURIComponent(producerSlugify(producerParentName(name))) + '/';
  }

  // ── Sports et conferences ─────────────────────────────────────────────
  // Miroir de generate_seo_pages.is_non_concert (meme liste, meme sigle en
  // mot entier) : ces dates n'ont pas de fiche /concert/ et ne comptent pour
  // aucun producteur. producteur.js et producteurs.js s'en servent pour
  // garder le perimetre des pages producteur pre-rendues
  // (scrapers/producteur_seo.in_scope) ; tests/test_producer_france.py
  // verifie que les deux listes restent les memes.
  var NON_CONCERT_KEYWORDS = ['BASKET', 'RUGBY', 'ENTREPRENEURS', 'RACING 92',
    'STADE FRANCAIS', 'COUPE DE FRANCE', 'VOLLEY', 'HANDBALL', 'FOOTBALL', 'PROGRAMMATION'];
  function isNonConcert(name) {
    var up = String(name || '').toUpperCase();
    if (/\bMMA\b/.test(up)) return true;
    for (var i = 0; i < NON_CONCERT_KEYWORDS.length; i++) {
      if (up.indexOf(NON_CONCERT_KEYWORDS[i]) !== -1) return true;
    }
    return false;
  }

  // ── Artiste d'une date ────────────────────────────────────────────────
  // « Florent Pagny - la Suite du Retour » est un concert de Florent Pagny :
  // l'export pose `artist` quand le titre porte un nom de spectacle
  // (export_to_json._set_artist, meme regle que l'onglet Tour Intelligence).
  // Toute page qui deduit un artiste d'une date passe par ici ; le titre du
  // concert, lui, reste `event_name`. Miroir : artist_name_of (generate_seo_pages).
  // `artist` present et VIDE : titre d'evenement sans artiste (« Soiree Soupes
  // - Janvier », scrapers/event_title_artist.py) — ni page artiste, ni lien.
  function artistName(e) {
    if (!e) return '';
    if (typeof e.artist === 'string') return e.artist;
    return e.event_name || '';
  }

  // Ou mene une date quand on ne peut pas mener a son artiste (titre
  // d'evenement, `artist` vide) : sa fiche publiee (slug arbitre par le
  // pre-rendu, window.__LAGENDA_SLUGS, comme artiste.js et salle.js), sinon
  // le lien de la date, comme compte.js. '' si rien : jamais « # ».
  function dateHref(e) {
    if (!e) return '';
    var table = global.__LAGENDA_SLUGS || {};
    var slug = table[String(e.id)];
    if (slug) return '/concert/' + encodeURIComponent(slug) + '/';
    return e.link || e.ticket_url || '';
  }

  // ── Concert « hors les murs » ─────────────────────────────────────────
  // La salle vend un concert qu'elle donne ailleurs (registre des erreurs
  // E12, decision du 2026-10-24) : la date reste celle de la salle (`venue`),
  // son vrai lieu est `lieu` (« L'Oppidum, Champagnole ») et son adresse
  // `lieu_adresse` ({nom, rue, code_postal, ville}), poses par l'export
  // (scrapers/hors_les_murs.py), absents pour une date a la salle. Miroirs :
  // hors_les_murs.label / address_line, au caractere pres.
  var OFF_SITE_LABEL = 'Hors les murs';
  function addressLine(a) {
    if (!a || typeof a !== 'object') return '';
    var town = [a.code_postal, a.ville].filter(Boolean).join(' ');
    return [a.rue, town].filter(Boolean).join(', ');
  }
  /** {lieu, label, address, where} d'une date hors les murs, null sinon.
   *  `where` : le lieu complet d'une entree d'agenda (add-to-calendar.js),
   *  « L'Oppidum, Rue de l'Égalité, 39300 Champagnole ». */
  function offSite(e) {
    var lieu = String((e && e.lieu) || '').replace(/\s+/g, ' ').trim();
    if (!lieu) return null;
    var address = addressLine(e.lieu_adresse);
    var nom = (e.lieu_adresse && e.lieu_adresse.nom) || lieu;
    return { lieu: lieu, label: OFF_SITE_LABEL + ' · ' + lieu, address: address,
             where: address ? nom + ', ' + address : lieu };
  }

  global.LAgendaFields = {
    offSite: offSite,
    addressLine: addressLine,
    OFF_SITE_LABEL: OFF_SITE_LABEL,
    artistName: artistName,
    dateHref: dateHref,
    isUncertain: isUncertain,
    stillSelling: stillSelling,
    soldOutSince: soldOutSince,
    soldOutLabel: soldOutLabel,
    newDate: newDate,
    channelSoldOut: channelSoldOut,
    channelCtaLabel: channelCtaLabel,
    frDate: frDate,
    fmtTime: fmtTime,
    timeLabel: timeLabel,
    fmtEuro: fmtEuro,
    parsePrice: parsePrice,
    ticketHref: ticketHref,
    resaleHref: resaleHref,
    soldOutCtaLabel: soldOutCtaLabel,
    ctaPlatform: ctaPlatform,
    platformLabel: platformLabel,
    isOwnTicketing: isOwnTicketing,
    ticketCtaLabel: ticketCtaLabel,
    OWN_TICKETING_LABEL: OWN_TICKETING_LABEL,
    RESALE_LABEL: RESALE_LABEL,
    isUsableTicketUrl: isUsableTicketUrl,
    isUpcoming: isUpcoming,
    upcomingOnly: upcomingOnly,
    loadObservedProducers: loadObservedProducers,
    keepObservedProducers: keepObservedProducers,
    producerList: producerList,
    loadProducerPages: loadProducerPages,
    hasProducerPage: hasProducerPage,
    USUAL_PRODUCER_LABEL: USUAL_PRODUCER_LABEL,
    USUAL_PRODUCER_TITLE: USUAL_PRODUCER_TITLE,
    loadProducerGroups: loadProducerGroups,
    producerSlugify: producerSlugify,
    producerParentName: producerParentName,
    producerHref: producerHref,
    isNonConcert: isNonConcert
  };
})(window);
